// 채팅 답변 뒤 화면이 실제로 한 일(실행·건너뜀·실패·진행 중)을 다음 요청에 실어 보낸다.
//   AI 는 자기 답변 뒤에 화면이 무엇을 했는지 모른다. 이 보고가 없으면 "안 됐어"에 원인을 지어내거나 같은 약속을 반복한다.
//   서버는 허용 목록(services/chat_turn_signals.normalize_client_report) 밖의 값을 버린다. 본문·파일명은 담지 않는다.
//   새로고침해도 이어지도록 sessionStorage 에 대화별로 하나(가장 최근 동작)만 둔다.

const STORAGE_KEY = 'whatquiz_chat_action_report';
const MAX_AGE_MS = 30 * 60 * 1000;
const DRAFT_KEY = 'draft';
const DRAFT_MAX_AGE_MS = 2 * 60 * 1000;
// 화면을 떠나면 '진행 중'이 끝 소식 없이 남을 수 있다. 이미지형 덱도 몇 분이면 끝나므로 그보다 오래된 '진행 중'은 버린다.
const RUNNING_MAX_AGE_MS = 8 * 60 * 1000;

const memory = {};

// 끝난 결과는 다음 요청을 기다리지 않고 바로도 보낸다(routes/chat_client_action). 새 탭에서 이어 가거나 30분 뒤 돌아와도
//   서버 기록에 남아 AI 가 "만들었는지" 안다. 보내는 쪽(토큰·주소)은 api/chatApi 가 붙인다.
const REPORT_NOW_RESULTS = new Set(['executed', 'skipped', 'failed']);
let sessionsByConversation = {};
let sendReportNow = null;

export function setChatActionSessions(map) {
  sessionsByConversation = map && typeof map === 'object' ? map : {};
}

export function configureChatActionReporter(send) {
  sendReportNow = typeof send === 'function' ? send : null;
}

function reportNow(conversationId, { action, target = '', result, reason = '' }) {
  if (!sendReportNow || !conversationId || !REPORT_NOW_RESULTS.has(result)) return;
  const sessionId = sessionsByConversation[conversationId];
  if (!sessionId) return;
  try {
    Promise.resolve(sendReportNow({ session_id: sessionId, last_action: { action, target, result, reason } }))
      .catch(() => {});
  } catch (_error) {
    // 바로 보내기는 덤이다 — 다음 요청 보고는 그대로 남는다
  }
}

function storage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_error) {
    return null;
  }
}

function readAll() {
  const store = storage();
  if (!store) return memory;
  try {
    const parsed = JSON.parse(store.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {};
  }
}

function writeAll(all) {
  const store = storage();
  if (!store) {
    Object.keys(memory).forEach((key) => { if (!(key in all)) delete memory[key]; });
    Object.assign(memory, all);
    return;
  }
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (_error) {
    // 저장 공간이 없으면 보고 없이 진행한다
  }
}

/**
 * @param {string|null} conversationId
 * @param {{action: string, target?: string, result: 'executed'|'requested'|'skipped'|'failed'|'running', reason?: string}} report
 */
export function recordChatAction(conversationId, { action, target = '', result, reason = '' } = {}, now = Date.now()) {
  if (!action || !result) return null;
  const all = readAll();
  const id = `${now}-${Math.random().toString(36).slice(2, 8)}`;
  all[conversationId || DRAFT_KEY] = { id, action, target, result, reason, at: now };
  Object.keys(all).forEach((key) => {
    if (!all[key] || now - Number(all[key].at || 0) > MAX_AGE_MS) delete all[key];
  });
  writeAll(all);
  reportNow(conversationId, { action, target, result, reason });
  return id;
}

/**
 * 'running'으로 남긴 보고를 실제 끝난 결과로 바꾼다. 그사이 다른 보고(건너뜀·실패)가 덮었거나
 * 이미 서버로 보냈으면 그대로 둔다 — 늦게 끝난 작업이 더 최근 사실을 지우지 않게.
 */
export function settleChatAction(conversationId, id, { result, reason = '' } = {}, now = Date.now()) {
  if (!id || !result) return;
  const all = readAll();
  const key = conversationId || DRAFT_KEY;
  const item = all[key];
  if (!item || item.id !== id) return;
  all[key] = { ...item, result, reason, at: now };
  writeAll(all);
  reportNow(conversationId, { action: item.action, target: item.target, result, reason });
}

/**
 * 오래 걸린 작업의 끝 결과를 남긴다. 그 보고가 아직 있으면 바꾸고, 이미 서버로 보냈거나 지워졌으면 끝 결과로 다시 남긴다.
 *   그사이 다른 보고가 들어왔으면 그대로 둔다 — 더 최근 사실을 지우지 않게.
 */
export function finishChatAction(conversationId, id, { action, target = '', result, reason = '' } = {}, now = Date.now()) {
  if (!id || !action || !result) return null;
  const all = readAll();
  const item = all[conversationId || DRAFT_KEY];
  if (item && item.id !== id) return null;
  if (item) {
    settleChatAction(conversationId, id, { result, reason }, now);
    return id;
  }
  return recordChatAction(conversationId, { action, target, result, reason }, now);
}

/**
 * 화면에서 그 작업이 더는 진행 중이 아니면 남아 있는 'running' 보고를 지운다.
 *   새로고침·이동으로 끊긴 작업이 "곧 나타나요"로 AI 에게 전달되지 않게 — 끊긴 대화 안 이미지는 이어지지 않는다.
 */
export function dropStaleRunningAction(conversationId, action) {
  if (!conversationId || !action) return false;
  const all = readAll();
  const item = all[conversationId];
  if (!item || item.action !== action || item.result !== 'running') return false;
  delete all[conversationId];
  writeAll(all);
  return true;
}

/** 다음 채팅 요청에 붙일 보고를 꺼낸다(한 번 보내면 지운다). 없거나 오래됐으면 null. */
export function takeChatActionReport(conversationId, now = Date.now()) {
  const all = readAll();
  // 대화 ID가 있으면 그 대화 것만. 'draft'(ID가 생기기 전 기록)는 다른 대화에 붙지 않게 ID 없는 요청에서만,
  //   그것도 방금 것만 쓴다.
  const key = conversationId ? (all[conversationId] ? conversationId : null) : (all[DRAFT_KEY] ? DRAFT_KEY : null);
  if (!key) return null;
  const item = all[key];
  delete all[key];
  writeAll(all);
  const ageMs = now - Number(item.at || 0);
  let maxAge = key === DRAFT_KEY ? DRAFT_MAX_AGE_MS : MAX_AGE_MS;
  if (item.result === 'running') maxAge = Math.min(maxAge, RUNNING_MAX_AGE_MS);
  if (!(ageMs >= 0) || ageMs > maxAge) return null;
  return {
    last_action: {
      action: item.action,
      target: item.target || '',
      result: item.result,
      reason: item.reason || '',
      age_s: Math.round(ageMs / 1000),
    },
  };
}

export function clearChatActionReports() {
  writeAll({});
}
